import React, { useCallback, useMemo, useState } from 'react';

import Papa from 'papaparse';

import { Close, Download, UploadFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { AdvertRanking } from '~/services/MerchandiseService';

interface ExportEnrichmentDialogProps {
  open: boolean;
  onClose: () => void;
  rankings: Array<AdvertRanking>;
}

interface EnrichedRanking extends AdvertRanking {
  priceParity: string | null;
  advertImpressions: number | null;
  productViews: number | null;
}

interface ViewsData {
  advertImpressions: number | null;
  productViews: number | null;
}

// Parse CSV file to a Map of advertId -> value using papaparse
const parseCsvToMap = (csvText: string, advertIdColumn: string, valueColumn: string): Map<string, string> => {
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
  });

  if (result.data.length === 0) return new Map();

  const headers = result.meta.fields ?? [];
  const advertIdKey = advertIdColumn.toLowerCase();
  const valueKey = valueColumn.toLowerCase();

  if (!headers.includes(advertIdKey) || !headers.includes(valueKey)) {
    throw new Error(`Required columns not found. Expected "${advertIdColumn}" and "${valueColumn}"`);
  }

  const map = new Map<string, string>();
  result.data.forEach((row) => {
    const advertId = row[advertIdKey];
    const value = row[valueKey];
    if (advertId && value) {
      map.set(advertId, value);
    }
  });

  return map;
};

// Parse views CSV file to a Map of advertId -> ViewsData using papaparse
const parseViewsCsvToMap = (csvText: string): Map<string, ViewsData> => {
  const result = Papa.parse<Record<string, string>>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header) => header.toLowerCase().trim(),
  });

  if (result.data.length === 0) return new Map();

  const headers = result.meta.fields ?? [];

  if (!headers.includes('offer_bk')) {
    throw new Error('Required column "offer_bk" not found');
  }
  if (!headers.includes('advert_impressions') && !headers.includes('product_views')) {
    throw new Error('At least one of "advert_impressions" or "product_views" columns required');
  }

  const map = new Map<string, ViewsData>();
  result.data.forEach((row) => {
    const advertId = row['offer_bk'];
    if (advertId) {
      const impressions = row['advert_impressions'];
      const views = row['product_views'];
      map.set(advertId, {
        advertImpressions: impressions ? Number(impressions) : null,
        productViews: views ? Number(views) : null,
      });
    }
  });

  return map;
};

// Generate CSV content from enriched rankings
const generateCsv = (rankings: Array<EnrichedRanking>): string => {
  const headers = [
    'Rank',
    'Title',
    'IsOnline',
    'BrandName',
    'CategoryName',
    'SalesCount',
    'PriceParity',
    'AdvertImpressions',
    'ProductViews',
    'CreatedAt',
    'UpdatedAt',
    'AdvertId',
  ];

  const rows = rankings.map((r) => [
    r.rank,
    r.title ?? '',
    r.isOnline === null ? '' : r.isOnline ? 'Yes' : 'No',
    r.brandName ?? '',
    r.categoryName ?? '',
    r.salesCount ?? '',
    r.priceParity ?? '',
    r.advertImpressions ?? '',
    r.productViews ?? '',
    r.createdAt ?? '',
    r.updatedAt ?? '',
    r.advertId,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape cells that contain commas, quotes, or newlines
          const str = String(cell);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(','),
    ),
  ].join('\n');

  return csvContent;
};

export default function ExportEnrichmentDialog({ open, onClose, rankings }: ExportEnrichmentDialogProps) {
  const [priceParityFile, setPriceParityFile] = useState<File | null>(null);
  const [viewsFile, setViewsFile] = useState<File | null>(null);
  const [priceParityMap, setPriceParityMap] = useState<Map<string, string>>(new Map());
  const [viewsMap, setViewsMap] = useState<Map<string, ViewsData>>(new Map());
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = useCallback(async (file: File, type: 'priceParity' | 'views') => {
    try {
      setError(null);
      const text = await file.text();

      if (type === 'priceParity') {
        // Expect columns: advert_id, price_parity_percentage
        const map = parseCsvToMap(text, 'advert_id', 'price_parity_percentage');
        setPriceParityMap(map);
        setPriceParityFile(file);
      } else {
        // Expect columns: offer_bk, advert_impressions, product_views
        const map = parseViewsCsvToMap(text);
        setViewsMap(map);
        setViewsFile(file);
      }
    } catch (err) {
      setError(`Failed to parse ${type} CSV: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, []);

  const enrichedRankings = useMemo((): Array<EnrichedRanking> => {
    return rankings.map((r) => {
      const viewsData = viewsMap.get(r.advertId);
      return {
        ...r,
        priceParity: priceParityMap.get(r.advertId) ?? null,
        advertImpressions: viewsData?.advertImpressions ?? null,
        productViews: viewsData?.productViews ?? null,
      };
    });
  }, [rankings, priceParityMap, viewsMap]);

  const handleDownload = useCallback(() => {
    const csvContent = generateCsv(enrichedRankings);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ranking_export_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [enrichedRankings]);

  const handleClose = () => {
    setPriceParityFile(null);
    setViewsFile(null);
    setPriceParityMap(new Map());
    setViewsMap(new Map());
    setError(null);
    onClose();
  };

  // Preview stats - memoized to avoid recalculating on every render
  const matchedPriceParity = useMemo(
    () => enrichedRankings.filter((r) => r.priceParity !== null).length,
    [enrichedRankings],
  );
  const matchedViews = useMemo(
    () => enrichedRankings.filter((r) => r.advertImpressions !== null || r.productViews !== null).length,
    [enrichedRankings],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          Export Rankings with Enrichment Data
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Upload CSV files to enrich ranking data with price parity and views information.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction="row" spacing={4} mb={3}>
          {/* Price Parity Upload */}
          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              Price Parity CSV
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Expected columns: <code>advert_id</code>, <code>price_parity_percentage</code>
            </Typography>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              {priceParityFile ? priceParityFile.name : 'Upload File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file, 'priceParity');
                  // Reset input so selecting the same file triggers onChange again
                  e.target.value = '';
                }}
              />
            </Button>
            {priceParityFile && (
              <Typography variant="body2" color="success.main" mt={1}>
                Matched: {matchedPriceParity} / {rankings.length} adverts
              </Typography>
            )}
          </Box>

          {/* Views Upload */}
          <Box flex={1}>
            <Typography variant="subtitle2" gutterBottom>
              Views CSV
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Expected columns: <code>offer_bk</code>, <code>advert_impressions</code>, <code>product_views</code>
            </Typography>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              {viewsFile ? viewsFile.name : 'Upload File'}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file, 'views');
                  // Reset input so selecting the same file triggers onChange again
                  e.target.value = '';
                }}
              />
            </Button>
            {viewsFile && (
              <Typography variant="body2" color="success.main" mt={1}>
                Matched: {matchedViews} / {rankings.length} adverts
              </Typography>
            )}
          </Box>
        </Stack>

        {/* Preview Table */}
        <Typography variant="subtitle2" gutterBottom>
          Preview (first 10 rows)
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Rank</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Sales</TableCell>
                <TableCell>Price Parity</TableCell>
                <TableCell>Impressions</TableCell>
                <TableCell>Product Views</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrichedRankings.slice(0, 10).map((row) => (
                <TableRow key={row.advertId}>
                  <TableCell>{row.rank}</TableCell>
                  <TableCell>{row.title ?? 'N/A'}</TableCell>
                  <TableCell>{row.brandName ?? 'N/A'}</TableCell>
                  <TableCell>{row.salesCount ?? 'N/A'}</TableCell>
                  <TableCell
                    sx={{
                      color: row.priceParity ? 'success.main' : 'text.disabled',
                      fontWeight: row.priceParity ? 600 : 400,
                    }}
                  >
                    {row.priceParity ?? '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: row.advertImpressions !== null ? 'success.main' : 'text.disabled',
                      fontWeight: row.advertImpressions !== null ? 600 : 400,
                    }}
                  >
                    {row.advertImpressions !== null ? row.advertImpressions.toLocaleString() : '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: row.productViews !== null ? 'success.main' : 'text.disabled',
                      fontWeight: row.productViews !== null ? 600 : 400,
                    }}
                  >
                    {row.productViews !== null ? row.productViews.toLocaleString() : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="text">
          Cancel
        </Button>
        <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleDownload}>
          Download Enriched CSV
        </Button>
      </DialogActions>
    </Dialog>
  );
}
